import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
  NbCardModule,
  NbInputModule,
  NbSpinnerModule,
  NbButtonModule,
  NbSelectModule,
  NbOptionModule,
  NbDatepickerModule,
  NbDialogService
} from "@nebular/theme";
import { TranslateModule } from "@ngx-translate/core";
import { KPI } from "../../../../@core/api";
import { getColorHex } from "../../../../@core/utils/color.utils";
import { ComplianceService } from "../../../../@core/utils/compliance.service";
import { AssetsService } from "../../../../@core/utils/assets.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import moment from "moment";
import { Observable, of } from "rxjs";
import { map, catchError, first } from "rxjs/operators";
import { ControlReportDialogComponent } from "../../control-report-dialog/control-report-dialog.component";
import { ControlReportListDialogComponent } from "../../control-report-list-dialog/control-report-list-dialog.component";
import { toDateValue, toIsoDate } from "../../../utils/date.utils";


@Component({
  selector: "app-compliance-tab",
  templateUrl: "./compliance-tab.component.html",
  styleUrls: ["./compliance-tab.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbButtonModule,
    FontAwesomeModule,
    NbInputModule,
    TranslateModule,
    NbSelectModule,
    NbOptionModule,
    NbDatepickerModule,
  ],
  providers: [DatePipe]
})
export class ComplianceTabComponent implements OnInit, OnChanges {
  @Input() public asset: KPI.MappedAsset;
  @Input() public loading: boolean = false;
  @Input() public complianceManagementEnabled: boolean = false;
  @Input() public equipmentValidationEnabled: boolean = false;
  @Output() public formChange = new EventEmitter<{ form: UntypedFormGroup; initialValues: any }>();

  public form: UntypedFormGroup;
  public initialValues: any;
  public reserves: Array<{ 
    id?: number; 
    date_emission: string; 
    libelle: string;
    stade?: string;
    criticite?: string;
    responsable?: string;
    date_cloture?: string;
  }> = [];
  public complianceRecordId: number | null = null;
  public linkedReports: KPI.ControlOfficeReport[] = [];
  public latestReport: KPI.ControlOfficeReport | null = null;
  public hasReport: boolean = false;
  public readonly toDateValue = toDateValue;
  private readonly toIsoDate = toIsoDate;

  protected NUMBER_VALIDATORS = [
    Validators.pattern(/^-?\d+\.?\d*$/),
    Validators.pattern(/^(?!-0(\.0+)?$)-?(0|[1-9]\d*)(\.\d+)?$/)
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe,
    private complianceService: ComplianceService,
    private dialogService: NbDialogService,
    private assetsService: AssetsService
  ) {}

  ngOnInit(): void {
    if (this.asset) {
      this.loadReserves();
      this.initializeForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["asset"] && this.asset) {
      this.loadReserves();
      this.loadLinkedReports();
      this.initializeForm();
    }
  }

  private loadReserves(): void {
    this.reserves = [];
    if ((this.asset as any).compliance?.reservations) {
      this.reserves = (this.asset as any).compliance.reservations.map((reservation: any) => ({
        id: reservation.id,
        date_emission: reservation.issue_date || "",
        libelle: reservation.label || "",
        stade: reservation.status || reservation.stade || "",
        criticite: reservation.criticality || reservation.criticite || "",
        responsable: reservation.responsible || reservation.responsable || "",
        date_cloture: reservation.closing_date || reservation.date_cloture || ""
      }));
    }
  }

  private loadLinkedReports(): void {
    if (!this.asset?.id) {
      this.linkedReports = [];
      this.latestReport = null;
      this.hasReport = false;
      return;
    }

    this.complianceService.listControlOfficeReports({ linked_equipment: this.asset.id })
      .pipe(
        map(reports => {
          const filteredReports = reports.filter(report => 
            report.linked_equipment && 
            Array.isArray(report.linked_equipment) && 
            report.linked_equipment.includes(this.asset.id)
          );
          
          return filteredReports.sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA;
          });
        }),
        catchError(error => {
          console.error("Error loading linked reports:", error);
          return of([]);
        })
      )
      .subscribe(reports => {
        this.linkedReports = reports;
        this.latestReport = reports.length > 0 ? reports[0] : null;
        this.hasReport = reports.length > 0;
        this.cdr.detectChanges();
        
        if (this.latestReport && this.form) {
          const reportDate = this.latestReport.date;
          const currentDate = this.form.get("last_control_date")?.value;
          
          if (!currentDate || (reportDate && new Date(reportDate) > new Date(currentDate))) {
            this.form.get("last_control_date")?.setValue(reportDate);
            if (this.initialValues) {
              let normalizedDate: string | null = null;
              if (reportDate) {
                if (typeof reportDate === 'object' && (reportDate as any)._isAMomentObject) {
                  normalizedDate = (reportDate as any).format('YYYY-MM-DD');
                } else if (typeof reportDate === 'string') {
                  const date = new Date(reportDate);
                  if (!isNaN(date.getTime())) {
                    normalizedDate = this.datePipe.transform(date, "yyyy-MM-dd") || null;
                  }
                } else {
                  const dateObj = reportDate as any;
                  if (dateObj && typeof dateObj.getTime === 'function') {
                    normalizedDate = this.datePipe.transform(dateObj, "yyyy-MM-dd") || null;
                  }
                }
              }
              this.initialValues.last_control_date = normalizedDate;
              
              this.formChange.emit({ form: this.form, initialValues: this.initialValues });
            }
          }
        }
        
        this.cdr.detectChanges();
      });
  }

  private initializeForm(): void {
    const compliance = (this.asset as any).compliance;

    this.complianceRecordId = compliance && compliance.id && compliance.id > 0 ? compliance.id : null;
    const normalizeForInitial = (val: any): string | null => {
      if (val === null || val === undefined || val === '') return '';
      return val;
    };

    this.initialValues = {
      control_frequency: normalizeForInitial(compliance?.control_frequency),
      description: normalizeForInitial(compliance?.control_description),
      last_control_date: compliance?.last_control_date || null,
      environmental_compliance:
        compliance?.environmental_compliance !== undefined && compliance?.environmental_compliance !== null
          ? (compliance.environmental_compliance ? "true" : "false")
          : ""
    };

    this.loadReserves();

    this.form = new UntypedFormGroup({
      control_frequency: new UntypedFormControl({ value: this.initialValues.control_frequency || "", disabled: false }),
      description: new UntypedFormControl({ value: this.initialValues.description || "", disabled: false }),
      last_control_date: new UntypedFormControl({
        value: this.initialValues.last_control_date,
        disabled: false
      }),
      environmental_compliance: new UntypedFormControl({
        value: this.initialValues.environmental_compliance || "",
        disabled: false
      })
    });

    setTimeout(() => {
      const updatedInitialValues = { ...this.initialValues };
      const lastControlDateControl = this.form.get('last_control_date');
      if (lastControlDateControl && lastControlDateControl.value) {
        const value = lastControlDateControl.value;
        if (value && typeof value === 'object' && (value as any)._isAMomentObject) {
          updatedInitialValues.last_control_date = (value as any).format('YYYY-MM-DD');
        }
      }
      
      this.initialValues = updatedInitialValues;
      this.formChange.emit({ form: this.form, initialValues: this.initialValues });
      
      const complianceFields = [
        'control_frequency',
        'last_control_date',
        'environmental_compliance'
      ];
      
      complianceFields.forEach(fieldName => {
        const control = this.form.get(fieldName);
        if (control) {
          control.valueChanges.subscribe(() => {
            this.formChange.emit({ form: this.form, initialValues: this.initialValues });
          });
        }
      });
    }, 0);
  }

  public formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const m = moment(dateString, 'YYYY-MM-DD', true);
    return m.isValid() ? m.format('D MMM YYYY') : '-';
  }

  public onDatepickerDateChange(date: Date, fieldName: string): void {
    this.form.get(fieldName)?.setValue(this.toIsoDate(date));
    this.cdr.detectChanges();
  }

  public openControlReportListDialog(): void {
    this.dialogService.open(ControlReportListDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: true,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'control-report-list-dialog-overlay',
      context: {
        asset: this.asset,
        onReportAttached: () => {
          if (this.asset?.id) {
            this.assetsService.getAsset(this.asset.id).pipe(first()).subscribe(updatedAsset => {
              if (updatedAsset) {
                this.asset = { ...updatedAsset as any } as KPI.MappedAsset;
                this.ngOnChanges({
                  asset: { 
                    currentValue: this.asset, 
                    previousValue: null, 
                    firstChange: false, 
                    isFirstChange: () => false 
                  }
                });
                this.loadLinkedReports();
                this.cdr.detectChanges();
              }
            });
          }
        }
      } as any
    });
  }

  public openControlReportDialog(): void {
    const lastControlDate = this.form?.get("last_control_date")?.value;
    const reportId = this.latestReport?.id || null;
    
    const dialog = this.dialogService.open(ControlReportDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'control-report-dialog-overlay',
      context: {
        lastControlDate: lastControlDate,
        asset: this.asset,
        reportId: reportId,
        latestReport: this.latestReport,
        onReportSaved: () => {
          if (this.asset?.id) {
            this.assetsService.getAsset(this.asset.id).pipe(first()).subscribe(updatedAsset => {
              if (updatedAsset) {
                this.asset = { ...updatedAsset as any } as KPI.MappedAsset;
                this.loadReserves();
                this.loadLinkedReports();
                this.cdr.detectChanges();
              }
            });
          } else {
            this.loadLinkedReports();
            this.cdr.detectChanges();
          }
        }
      } as any
    });
    
    dialog.onClose.subscribe(() => {
      if (this.asset?.id) {
        this.assetsService.getAsset(this.asset.id).pipe(first()).subscribe(updatedAsset => {
          if (updatedAsset) {
            this.asset = { ...updatedAsset as any } as KPI.MappedAsset;
            this.loadReserves();
            this.loadLinkedReports();
            this.cdr.detectChanges();
          }
        });
      } else {
        this.loadLinkedReports();
        this.cdr.detectChanges();
      }
    });
  }


  public isRegulatoryControlOverdue(): boolean {
    const frequency = this.form?.get("control_frequency")?.value;
    const lastControlDate = this.form?.get("last_control_date")?.value;

    if (!frequency || !lastControlDate || frequency === "-") {
      return false;
    }

    const lastControl = new Date(lastControlDate);
    const today = new Date();

    if (isNaN(lastControl.getTime())) {
      return false;
    }

    const diffTime = today.getTime() - lastControl.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    const diffYears = diffDays / 365.25;

    switch (frequency) {
      case "annuelle":
      case "1 time per year":
        return diffYears >= 1;
      case "1 time every 2 years":
        return diffYears >= 2;
      case "1 time every 3 years":
        return diffYears >= 3;
      case "1 time every 5 years":
        return diffYears >= 5;
      case "1 time every 10 years":
        return diffYears >= 10;
      default:
        return false;
    }
  }

  public getComplianceLastControlDateColor(): string {
    const compliance = (this.asset as any).compliance;
    const color = getColorHex(compliance?.last_control_date_color);
    return color;
  }

  public getComplianceReservationsColor(): string {
    const compliance = (this.asset as any).compliance;
    
    if (!this.complianceManagementEnabled) {
      const reservationsCount = compliance?.reservations_count || 0;
      if (reservationsCount > 0) {
        return '#FF0000';
      }
      const color = getColorHex(compliance?.reservations_color);
      return color;
    }
    const color = getColorHex(compliance?.reservations_color);
    return color;
  }

  public getComplianceEnvironmentalComplianceColor(): string {
    const compliance = (this.asset as any).compliance;
    const color = getColorHex(compliance?.environmental_compliance_color);
    return color;
  }

  public isColorWhite(color: string): boolean {
    return !color || color === "#FFFFFF" || color === "#ffffff" || color === "white";
  }

  public getFieldBackgroundColor(_fieldName: string): string {
    return '';
  }

  public isFieldMissing(fieldName: string): boolean {
    if (!this.equipmentValidationEnabled || !this.complianceManagementEnabled) {
      return false;
    }

    const value = this.form?.get(fieldName)?.value;
    const compliance = (this.asset as any).compliance;

    if (!compliance || compliance === null) {
      return false;
    }

    switch (fieldName) {
      case 'last_control_date':
        return !value || value === null;
      case 'control_frequency':
      case 'environmental_compliance':
        return !value || value === '' || value === null;
      default:
        return !value || value === '';
    }
  }

  public getMissingDataCount(): number {
    if (!this.complianceManagementEnabled) {
      return 0;
    }
    
    if (!this.form) {
      return 0;
    }

    let missingCount = 0;
    
    const fieldsToCheck = [
      'control_frequency',
      'environmental_compliance',
      'last_control_date'
    ];

    fieldsToCheck.forEach(fieldName => {
      const value = this.form.get(fieldName)?.value;
      
      if (fieldName === 'last_control_date') {
        if (!value || value === null) {
          missingCount++;
        }
      } else {
        if (!value || value === '' || value === null) {
          missingCount++;
        }
      }
    });

    return missingCount;
  }

  public createComplianceRecord(): Observable<KPI.AssetCompliance> {
    const payload = this.complianceService.mapFormToPayload(this.form.value, this.asset.id);
    return this.complianceService.createAssetCompliance(payload);
  }

  public updateComplianceRecord(): Observable<KPI.AssetCompliance> {
    if (!this.complianceRecordId) {
      throw new Error("No compliance record ID available for update");
    }
    const payload = this.complianceService.mapFormToUpdatePayload(this.form.value, this.asset.id);
    return this.complianceService.updateAssetCompliance(this.complianceRecordId, payload);
  }

  public saveComplianceRecord(): Observable<KPI.AssetCompliance> {
    if (this.complianceRecordId) {
      return this.updateComplianceRecord();
    } else {
      return this.createComplianceRecord();
    }
  }

  public saveReservations(): Observable<any> {
    const newReserves = this.reserves.filter(reserve => !reserve.id);
    const existingReserves = this.reserves.filter(reserve => reserve.id);
    const deletedReserveIds: number[] = [];

    const operations: Observable<any>[] = [];

    if (newReserves.length > 0) {
      const createPayload: KPI.ReservationsBulkCreatePayload = {
        asset_id: this.asset.id,
        reservations: this.complianceService.mapReservationsToPayload(newReserves)
      };
      operations.push(this.complianceService.bulkCreateReservations(createPayload));
    }

    if (existingReserves.length > 0) {
      const updatePayloads = this.complianceService.mapReservationsToUpdatePayload(existingReserves);
      if (updatePayloads.length > 0) {
        operations.push(this.complianceService.bulkUpdateReservations(updatePayloads));
      }
    }

    if (deletedReserveIds.length > 0) {
      operations.push(this.complianceService.bulkDeleteReservations(deletedReserveIds));
    }

    if (operations.length === 0) {
      return new Observable(observer => {
        observer.next(null);
        observer.complete();
      });
    }

    return new Observable(observer => {
      let completedOperations = 0;
      const results: any[] = [];

      operations.forEach((operation, index) => {
        operation.subscribe({
          next: result => {
            results[index] = result;
            completedOperations++;

            if (completedOperations === operations.length) {
              observer.next(results);
              observer.complete();
            }
          },
          error: error => {
            observer.error(error);
          }
        });
      });
    });
  }
}
